import { useEffect, useRef, useState } from 'react';
import { FiCalendar } from 'react-icons/fi';
import SimpleCalendar from '../atoms/SimpleCalendar';
import InputFrame from '../atoms/InputFrame';

interface DateFieldProps {
    label: string;
    // Valor no formato ISO (yyyy-mm-dd), como o backend espera.
    value: string;
    onChange: (isoDate: string) => void;
    errorMessage?: string;
    disabled?: boolean;
    className?: string;
    id?: string;
}

const isoToDisplay = (iso: string): string => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    if (!y || !m || !d) return '';
    return `${d}/${m}/${y}`;
};

const displayToIso = (display: string): string => {
    const digits = display.replace(/\D/g, '');
    if (digits.length !== 8) return '';
    const d = digits.slice(0, 2);
    const m = digits.slice(2, 4);
    const y = digits.slice(4, 8);
    const day = Number(d);
    const month = Number(m);
    const year = Number(y);
    // Valida a data de verdade: o JS faz roll-over (31/02 vira 03/03), então se o Date
    // construído não devolver os mesmos dia/mês/ano, a data não existe.
    const date = new Date(year, month - 1, day);
    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day
    ) {
        return '';
    }
    return `${y}-${m}-${d}`;
};

// Aplica a máscara dd/mm/aaaa enquanto digita.
const maskInput = (raw: string): string => {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    const parts = [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)];
    return parts.filter(Boolean).join('/');
};

const isoToDate = (iso: string): Date | null => {
    if (!iso) return null;
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
};

const dateToIso = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const DateField = ({ label, value, onChange, errorMessage, disabled = false, className, id }: DateFieldProps) => {
    const [text, setText] = useState(isoToDisplay(value));
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Mantém o texto em sincronia quando o valor muda por fora (ex.: seleção no calendário).
    useEffect(() => {
        setText(isoToDisplay(value));
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleTyping = (raw: string) => {
        const masked = maskInput(raw);
        setText(masked);
        const iso = displayToIso(masked);
        // Só propaga quando a data está completa e válida; parcial não zera o valor.
        if (iso) onChange(iso);
        if (masked === '') onChange('');
    };

    const handlePick = (date: Date) => {
        onChange(dateToIso(date));
        setIsOpen(false);
    };

    let textColor = 'text-gray-900 dark:text-white';
    let focusColor = 'focus:border-blue-600 dark:focus:border-blue-400';
    if (errorMessage) {
        textColor = 'text-red-700 dark:text-red-400';
        focusColor = 'focus:border-red-600 dark:focus:border-red-400';
    }

    return (
        <div className="relative" ref={containerRef}>
            <InputFrame
                label={label}
                htmlFor={id}
                errorMessage={errorMessage}
                className={`mb-4 ${className ?? ''}`}
                textColor={textColor}
            >
                <input
                    id={id}
                    type="text"
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    value={text}
                    disabled={disabled}
                    onChange={(e) => handleTyping(e.target.value)}
                    className={`block py-2.5 px-0 w-full text-sm ${textColor} bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 ${focusColor} peer disabled:opacity-50 disabled:cursor-not-allowed`}
                />
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setIsOpen((o) => !o)}
                    className="ml-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
                    aria-label="Abrir calendário"
                >
                    <FiCalendar size={16} />
                </button>
            </InputFrame>

            {isOpen && (
                <div className="absolute z-30 mt-1 left-0">
                    <SimpleCalendar selectedDate={isoToDate(value)} onDateChange={handlePick} />
                </div>
            )}
        </div>
    );
};

export default DateField;
